/**
 * Lossless canonical serialization of structured-clone values, plus the
 * digest scheme shared by export.ts, import.ts, and (mirrored, with a
 * binding comment) scripts/decrypt-content-escrow.mjs.
 *
 * Encoding rules:
 * - Plain objects keep only PRESENT own enumerable keys (absent stays
 *   absent; `null` stays literal null), so the null-vs-absent distinction is
 *   preserved exactly.
 * - Property values that structured clone preserved as `undefined` encode
 *   as { $escrow: 'undefined' }.
 * - Blob        -> { $escrow: 'blob', mime, byteLength, sha256, base64 }
 * - ArrayBuffer -> { $escrow: 'arraybuffer', byteLength, sha256, base64 }
 * - TypedArray  -> { $escrow: 'typedarray', ctor, byteLength, sha256, base64 }
 * - Date        -> { $escrow: 'date', iso } (invalid dates: iso null)
 * - NaN / +-Infinity -> { $escrow: 'number', value: 'NaN' | 'Infinity' | '-Infinity' }
 * - Map / Set   -> { $escrow: 'map' | 'set', ... } (defensive; IDB permits them)
 * - Any object that already carries a key beginning '$escrow' is wrapped as
 *   { $escrow: 'literal', value } so decoding stays unambiguous.
 * - Unknown/forward-compatible plain properties pass through untouched.
 *
 * canonicalStringify emits JSON with object keys SORTED, so byte output is
 * deterministic for equal trees.
 *
 * LOSS IS COUNTED, NOT SWALLOWED. Two branches below cannot represent their
 * input and emit a { $escrow: 'unsupported' } placeholder instead. Those
 * bytes are gone, and every downstream digest still recomputes cleanly over
 * the placeholder, so the archive would verify green with content missing.
 * Each occurrence therefore increments a module counter that captureStore
 * samples around its own encode work and turns into a hard
 * EscrowError('unrepresentable-value'). No live store holds such a value
 * today (plain JSON-shaped rows and Blobs, all of which encode faithfully);
 * the counter exists because this module is the last line of defence for
 * content that may exist only on one device, and its previous answer to an
 * unrepresentable value was to drop it and report success.
 */

import type { EncodedValue, EscrowManifest, EscrowRow, EscrowStore } from './types';

// ---------------------------------------------------------------------------
// base64 + sha256 (browser/Node shared)
// ---------------------------------------------------------------------------

export function bytesToBase64(bytes: Uint8Array): string {
  if (typeof btoa === 'function') {
    let bin = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(bin);
  }
  // Node fallback (no static 'node:*' import so bundlers never see it).
  const buf = (globalThis as Record<string, unknown>).Buffer as
    | { from(b: Uint8Array): { toString(enc: string): string } }
    | undefined;
  if (!buf) throw new Error('no-base64-codec');
  return buf.from(bytes).toString('base64');
}

export function base64ToBytes(b64: string): Uint8Array {
  if (typeof atob === 'function') {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }
  const buf = (globalThis as Record<string, unknown>).Buffer as
    | { from(s: string, enc: string): Uint8Array }
    | undefined;
  if (!buf) throw new Error('no-base64-codec');
  return new Uint8Array(buf.from(b64, 'base64'));
}

let subtlePromise: Promise<SubtleCrypto> | null = null;

/** crypto.subtle, with a node:crypto webcrypto fallback (dynamic, unbundled). */
export function getSubtle(): Promise<SubtleCrypto> {
  if (!subtlePromise) {
    subtlePromise = (async () => {
      const g = globalThis as { crypto?: Crypto };
      if (g.crypto?.subtle) return g.crypto.subtle;
      // Indirect dynamic import so browser bundlers never resolve 'node:crypto'.
      const dynamicImport = new Function('m', 'return import(m)') as (
        m: string,
      ) => Promise<{ webcrypto: Crypto }>;
      const mod = await dynamicImport('node:crypto');
      return mod.webcrypto.subtle;
    })();
  }
  return subtlePromise;
}

export async function sha256Hex(input: Uint8Array | string): Promise<string> {
  const bytes = typeof input === 'string' ? new TextEncoder().encode(input) : input;
  const subtle = await getSubtle();
  const src = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
  const digest = new Uint8Array(await subtle.digest('SHA-256', src as ArrayBuffer));
  let hex = '';
  for (const b of digest) hex += b.toString(16).padStart(2, '0');
  return hex;
}

// ---------------------------------------------------------------------------
// Canonical encoding
// ---------------------------------------------------------------------------

/**
 * Count of values encodeCanonical could not represent (see the header note).
 * Monotonic for the process; callers sample it as a DELTA around their own
 * encode work rather than resetting it, so two callers can never race a
 * reset and mask each other's loss.
 */
let unsupportedEncounters = 0;

/** Current value of the unrepresentable-value counter. Sample as a delta. */
export function unsupportedEncounterCount(): number {
  return unsupportedEncounters;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (v === null || typeof v !== 'object') return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

async function encodeBytes(
  tag: 'blob' | 'arraybuffer' | 'typedarray',
  bytes: Uint8Array,
  extra: Record<string, unknown>,
): Promise<EncodedValue> {
  return {
    $escrow: tag,
    byteLength: bytes.byteLength,
    sha256: await sha256Hex(bytes),
    base64: bytesToBase64(bytes),
    ...extra,
  };
}

/** Encode a structured-clone value into a JSON-safe tagged tree. */
export async function encodeCanonical(value: unknown): Promise<EncodedValue> {
  if (value === undefined) return { $escrow: 'undefined' };
  if (value === null) return null;
  const t = typeof value;
  if (t === 'string' || t === 'boolean') return value;
  if (t === 'number') {
    const n = value as number;
    if (Number.isNaN(n)) return { $escrow: 'number', value: 'NaN' };
    if (n === Infinity) return { $escrow: 'number', value: 'Infinity' };
    if (n === -Infinity) return { $escrow: 'number', value: '-Infinity' };
    return n;
  }
  if (t === 'bigint') return { $escrow: 'bigint', value: (value as bigint).toString() };
  if (t !== 'object') {
    // function/symbol are not structured-clonable; tag defensively.
    unsupportedEncounters++;
    return { $escrow: 'unsupported', typeof: t };
  }
  if (value instanceof Date) {
    const time = value.getTime();
    return { $escrow: 'date', iso: Number.isNaN(time) ? null : value.toISOString() };
  }
  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    const bytes = new Uint8Array(await value.arrayBuffer());
    return encodeBytes('blob', bytes, { mime: value.type });
  }
  if (value instanceof ArrayBuffer) {
    return encodeBytes('arraybuffer', new Uint8Array(value), {});
  }
  if (ArrayBuffer.isView(value)) {
    const view = value as ArrayBufferView;
    const bytes = new Uint8Array(view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength));
    return encodeBytes('typedarray', bytes, { ctor: value.constructor.name });
  }
  if (value instanceof Map) {
    const entries: EncodedValue[] = [];
    for (const [k, v] of value.entries()) {
      entries.push([await encodeCanonical(k), await encodeCanonical(v)]);
    }
    return { $escrow: 'map', entries };
  }
  if (value instanceof Set) {
    const values: EncodedValue[] = [];
    for (const v of value.values()) values.push(await encodeCanonical(v));
    return { $escrow: 'set', values };
  }
  if (Array.isArray(value)) {
    const out: EncodedValue[] = [];
    for (let i = 0; i < value.length; i++) {
      // Holes / undefined elements encode as the undefined tag.
      out.push(await encodeCanonical(i in value ? value[i] : undefined));
    }
    return out;
  }
  if (isPlainObject(value)) {
    // Dynamic structured-clone keys must never pass through Object.prototype.
    // An own `__proto__` property assigned into `{}` invokes the legacy setter
    // and disappears from Object.keys, producing a digest-valid lossy archive.
    const out = Object.create(null) as Record<string, EncodedValue>;
    for (const key of Object.keys(value)) {
      out[key] = await encodeCanonical(value[key]);
    }
    // Escape hatch: user data already carrying '$escrow'-prefixed keys.
    if (Object.keys(out).some((k) => k.startsWith('$escrow'))) {
      return { $escrow: 'literal', value: out };
    }
    return out;
  }
  // Exotic clonable (RegExp, File, etc.): tag with the constructor name only.
  unsupportedEncounters++;
  return { $escrow: 'unsupported', ctor: (value as object).constructor?.name ?? null };
}

/**
 * Deterministic JSON stringification of an already-encoded tree:
 * object keys sorted, arrays in order. Throws on non-finite numbers
 * (they must already be tagged by encodeCanonical).
 */
export function canonicalStringify(value: EncodedValue): string {
  if (value === null) return 'null';
  const t = typeof value;
  if (t === 'string') return JSON.stringify(value);
  if (t === 'boolean') return value ? 'true' : 'false';
  if (t === 'number') {
    if (!Number.isFinite(value as number)) throw new Error('non-finite-in-canonical');
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return '[' + value.map((v) => canonicalStringify(v)).join(',') + ']';
  }
  if (t === 'object') {
    const obj = value as Record<string, EncodedValue>;
    const keys = Object.keys(obj).sort();
    return (
      '{' + keys.map((k) => JSON.stringify(k) + ':' + canonicalStringify(obj[k])).join(',') + '}'
    );
  }
  throw new Error('unsupported-in-canonical');
}

// ---------------------------------------------------------------------------
// Digest scheme (single definition; import.ts + the CLI recompute with it)
// ---------------------------------------------------------------------------

export function computeRowDigest(key: EncodedValue, value: EncodedValue): Promise<string> {
  return sha256Hex(canonicalStringify({ key, value }));
}

export function computeKeySetDigest(encodedKeys: EncodedValue[]): Promise<string> {
  return sha256Hex(canonicalStringify(encodedKeys));
}

export function computeStoreContentDigest(store: {
  name: string;
  rowCount: number;
  keySetDigest: string;
  rowDigests?: string[];
}): Promise<string> {
  const base: Record<string, EncodedValue> = {
    name: store.name,
    rowCount: store.rowCount,
    keySetDigest: store.keySetDigest,
  };
  if (store.rowDigests) base.rowDigests = store.rowDigests;
  return sha256Hex(canonicalStringify(base));
}

function storeSkeleton(s: EscrowStore): Record<string, EncodedValue> {
  return {
    name: s.name,
    rowCount: s.rowCount,
    keySetDigest: s.keySetDigest,
    contentDigest: s.contentDigest,
    summaryOnly: s.summaryOnly === true,
  };
}

/** Manifest contentDigest: everything except contextId/captureStart/captureEnd/digests. */
export function computeManifestContentDigest(
  m: Omit<EscrowManifest, 'digests'>,
): Promise<string> {
  return sha256Hex(
    canonicalStringify({
      archiveVersion: m.archiveVersion,
      contextLabel: m.contextLabel,
      metadata: m.metadata as unknown as EncodedValue,
      stores: m.stores.map(storeSkeleton),
      localStorage: m.localStorage as unknown as EncodedValue,
    }),
  );
}

/** Full-manifest digest (everything except digests.manifestDigest itself). */
export function computeManifestDigest(
  m: Omit<EscrowManifest, 'digests'> & { digests: { contentDigest: string } },
): Promise<string> {
  return sha256Hex(
    canonicalStringify({
      archiveVersion: m.archiveVersion,
      contextId: m.contextId,
      contextLabel: m.contextLabel,
      captureStart: m.captureStart,
      captureEnd: m.captureEnd,
      metadata: m.metadata as unknown as EncodedValue,
      stores: m.stores as unknown as EncodedValue,
      localStorage: m.localStorage as unknown as EncodedValue,
      contentDigest: m.digests.contentDigest,
    }),
  );
}

export type { EscrowRow };
